"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import {
  type ParsedAbundances,
  parseAbundances,
} from "@/lib/tool-kit/ecology/abundance";
import {
  type SimpsonForm,
  type SimpsonResult,
  simpson,
} from "@/lib/tool-kit/ecology/simpson";

/** English strings. A translated content file overrides these through `ui`. */
export const SIMPSONS_DIVERSITY_INDEX_CALCULATOR_STRINGS = {
  dataLabel:
    "Species counts — one species per line, name then count (or just the numbers)",
  dataHint:
    "For example “Oak 25”, one per line. Zero counts are ignored. Whole numbers are needed for the n(n − 1) form.",
  exampleData: "Oak 25\nBeech 18\nBirch 12\nHazel 8\nRowan 5\nHolly 2",
  unnamedSpecies: "Species {n}",
  formLabel: "Formula for D",
  formFinite: "Finite sample: Σ n(n − 1) ÷ N(N − 1)",
  formProportion: "Proportions: Σ p²",
  results: "Results",
  richness: "Species (S)",
  total: "Individuals (N)",
  dominance: "D — chance two individuals are the same species",
  diversity: "1 − D — chance they are different species",
  inverse: "1 ÷ D — effective number of species",
  evenness: "Evenness (1 ÷ D) ÷ S",
  undefined: "undefined",
  formsHeading: "All the forms side by side",
  columnQuantity: "Quantity",
  columnNames: "Also called",
  columnFinite: "Σ n(n − 1) ÷ N(N − 1)",
  columnProportion: "Σ p²",
  rowD: "D",
  rowDNames: "Simpson's index, λ, dominance, concentration",
  rowDiversity: "1 − D",
  rowDiversityNames:
    "Gini–Simpson index, Simpson's index of diversity, probability of interspecific encounter",
  rowInverse: "1 ÷ D",
  rowInverseNames:
    "Inverse Simpson index, Simpson's reciprocal index, Hill number N₂",
  speciesHeading: "Species",
  columnSpecies: "Species",
  columnCount: "n",
  columnShare: "p = n ÷ N",
  columnSquare: "p²",
  columnPairs: "n(n − 1)",
  workingHeading: "The working",
  workingFinite:
    "D = Σ n(n − 1) ÷ (N(N − 1)) = {pairs} ÷ ({n} × {m}) = {result}",
  workingProportion: "D = Σ p² = {result}",
  workingDerived: "1 − D = {diversity}; 1 ÷ D = {inverse}",
  maxNote:
    "With {s} species the largest possible 1 − D is 1 − 1 ÷ S = {max}, reached when every species has the same count.",
  notWhole:
    "These abundances are not all whole numbers, so only the proportion form (Σ p²) can be used.",
  tooFew:
    "The finite-sample form needs at least two individuals in total, so only the proportion form is shown.",
  singleSpecies:
    "One species only: D = 1 and 1 − D = 0, so there is no diversity to measure.",
  zerosIgnored:
    "one: {count} species with a zero count was ignored.|other: {count} species with zero counts were ignored.",
  unreadable: "These lines had no count and were skipped: {lines}",
  negative: "Counts cannot be negative.",
  copyResult: "Copy results",
  copied: "Copied!",
  copySummary:
    "S = {s}, N = {n}; D = {d}, 1 − D = {diversity}, 1 ÷ D = {inverse} ({form})",
  enter: "Enter at least one species with a count above zero.",
} as const;

type Strings = typeof SIMPSONS_DIVERSITY_INDEX_CALCULATOR_STRINGS;
type Form = "finite" | "proportion";

const ID = "sd";

export default function SimpsonsDiversityIndexCalculatorWidget() {
  const strings = useToolUi(SIMPSONS_DIVERSITY_INDEX_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [form, setForm] = useState<Form>("finite");
  const parsed = parseAbundances(raw, (n) =>
    t(strings.unnamedSpecies, { n: fmt(n) })
  );
  const result = simpson(parsed.entries);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id={`${ID}-data`}
        label={strings.dataLabel}
        onChange={setRaw}
        prose
        rows={8}
        value={raw}
      />
      <PillGroup
        label={strings.formLabel}
        name={`${ID}-form`}
        onChange={setForm}
        options={[
          { value: "finite", label: strings.formFinite },
          { value: "proportion", label: strings.formProportion },
        ]}
        value={form}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Messages fmt={fmt} locale={locale} parsed={parsed} strings={strings} />
        {result ? (
          <Results
            fmt={fmt}
            form={form}
            parsed={parsed}
            result={result}
            strings={strings}
          />
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </div>
  );
}

function Messages({
  parsed,
  strings,
  fmt,
  locale,
}: {
  parsed: ParsedAbundances;
  strings: Strings;
  fmt: NumberFormat;
  locale: string;
}) {
  return (
    <>
      {parsed.negative ? <ErrorNotice>{strings.negative}</ErrorNotice> : null}
      {parsed.unreadable.length > 0 ? (
        <Notice>
          {t(strings.unreadable, { lines: parsed.unreadable.join("; ") })}
        </Notice>
      ) : null}
      {parsed.zeros > 0 ? (
        <Notice>
          {tPlural(strings.zerosIgnored, locale, parsed.zeros, {
            count: fmt(parsed.zeros),
          })}
        </Notice>
      ) : null}
    </>
  );
}

function formValue(
  value: number | null,
  strings: Strings,
  digits: (v: number) => string
): string {
  return value === null ? strings.undefined : digits(value);
}

function Results({
  result,
  parsed,
  form,
  strings,
  fmt,
}: {
  result: SimpsonResult;
  parsed: ParsedAbundances;
  form: Form;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const usable: Form =
    form === "finite" && result.finite ? "finite" : "proportion";
  const chosen: SimpsonForm =
    usable === "finite" && result.finite ? result.finite : result.proportion;
  const cell = (value: number | null) => formValue(value, strings, four);
  const formNote = (() => {
    if (form !== "finite" || result.finite) {
      return null;
    }
    return result.sumPairs === null && result.total >= 2
      ? strings.notWhole
      : strings.tooFew;
  })();
  const head = [
    strings.columnQuantity,
    strings.columnNames,
    strings.columnFinite,
    strings.columnProportion,
  ];
  const rows = [
    [
      strings.rowD,
      strings.rowDNames,
      result.finite ? cell(result.finite.dominance) : "—",
      cell(result.proportion.dominance),
    ],
    [
      strings.rowDiversity,
      strings.rowDiversityNames,
      result.finite ? cell(result.finite.diversity) : "—",
      cell(result.proportion.diversity),
    ],
    [
      strings.rowInverse,
      strings.rowInverseNames,
      result.finite ? cell(result.finite.inverse) : "—",
      cell(result.proportion.inverse),
    ],
  ];
  const speciesRows = parsed.entries.map((entry) => {
    const p = entry.count / result.total;
    return [
      entry.label,
      fmt(entry.count),
      four(p),
      four(p * p),
      Number.isInteger(entry.count)
        ? fmt(entry.count * (entry.count - 1))
        : "—",
    ];
  });
  const copy = t(strings.copySummary, {
    s: fmt(result.richness),
    n: fmt(result.total),
    d: cell(chosen.dominance),
    diversity: cell(chosen.diversity),
    inverse: cell(chosen.inverse),
    form: usable === "finite" ? strings.formFinite : strings.formProportion,
  });
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard label={strings.richness} value={fmt(result.richness)} />
        <StatCard label={strings.total} value={fmt(result.total)} />
        <StatCard
          emphasis
          label={strings.dominance}
          value={cell(chosen.dominance)}
        />
        <StatCard
          emphasis
          label={strings.diversity}
          value={cell(chosen.diversity)}
        />
        <StatCard label={strings.inverse} value={cell(chosen.inverse)} />
        <StatCard label={strings.evenness} value={cell(chosen.evenness)} />
      </StatGrid>
      {formNote ? <Notice>{formNote}</Notice> : null}
      {result.richness === 1 ? <Notice>{strings.singleSpecies}</Notice> : null}
      <p className={HINT}>
        {t(strings.maxNote, {
          s: fmt(result.richness),
          max: four(result.maxDiversity),
        })}
      </p>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.formsHeading}</h3>
        <DataTable head={head} rows={rows} />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.speciesHeading}</h3>
        <DataTable
          head={[
            strings.columnSpecies,
            strings.columnCount,
            strings.columnShare,
            strings.columnSquare,
            strings.columnPairs,
          ]}
          rows={speciesRows}
        />
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          {usable === "finite" && result.finite && result.sumPairs !== null ? (
            <li>
              {t(strings.workingFinite, {
                pairs: fmt(result.sumPairs),
                n: fmt(result.total),
                m: fmt(result.total - 1),
                result: four(result.finite.dominance),
              })}
            </li>
          ) : (
            <li>
              {t(strings.workingProportion, {
                result: four(result.proportion.dominance),
              })}
            </li>
          )}
          <li>
            {t(strings.workingDerived, {
              diversity: cell(chosen.diversity),
              inverse: cell(chosen.inverse),
            })}
          </li>
        </ul>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={copy}
      />
    </div>
  );
}
